import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Download, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import MarketingHeader from "@/components/MarketingHeader";

export default function AuditSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const [download, setDownload] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 12; // ~2 mins at 10s intervals

    const tick = async () => {
      attempts++;
      try {
        const r = await api.get(`/audit/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setDownload({
            audit_id: r.data.audit_id,
            token: r.data.pdf_token,
          });
          setState({ status: "paid" });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (r.data.payment_status === "error" || r.data.status === "not_found") {
          setState({ status: "error" });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: "timeout" });
          return;
        }
        setState({ status: "polling", attempts });
        setTimeout(tick, 10000);
      } catch (e) {
        // Backend or network error - try a few more then give up
        if (attempts >= maxAttempts) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "polling", attempts });
        setTimeout(tick, 10000);
      }
    };
    tick();
  }, [sessionId]);

  const downloadHref = download
    ? `${process.env.REACT_APP_BACKEND_URL}/api/audit/${download.audit_id}/pdf?token=${encodeURIComponent(download.token)}`
    : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showAudit={false} />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center" data-testid="audit-success">
          {state.status === "polling" && (
          <>
            <Loader2
              className="w-10 h-10 mx-auto animate-spin text-neutral-400"
              strokeWidth={1.5}
            />
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-6">
              Confirming your payment…
            </h1>
            <p className="text-neutral-500 text-sm mt-2 leading-relaxed">
              Stripe is finalizing the charge. Hang tight - this usually takes 5-15 seconds.
            </p>
            {state.attempts > 0 && (
              <p className="text-xs font-mono text-neutral-600 mt-4">
                attempt {state.attempts}
              </p>
            )}
          </>
        )}
        {state.status === "paid" && download && (
          <>
            <CheckCircle2
              className="w-12 h-12 mx-auto text-emerald-400"
              strokeWidth={1.5}
            />
            <h1 className="font-display text-3xl font-semibold tracking-tight mt-6">
              You're in.
            </h1>
            <p className="text-neutral-400 text-sm mt-3 leading-relaxed">
              Your full audit PDF is ready. The link is also accessible from this page -
              save it before closing.
            </p>
            <a
              href={downloadHref}
              download
              data-testid="audit-pdf-download"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-md font-medium text-sm hover:bg-neutral-200"
            >
              <Download className="w-4 h-4" strokeWidth={1.8} />
              Download PDF report
            </a>
            <p className="text-xs text-neutral-600 font-mono mt-6">
              Want to run more audits + ship campaigns?{" "}
              <a href="/register" className="text-white hover:underline">
                Create an account
              </a>
              .
            </p>
          </>
        )}
        {state.status === "expired" && (
          <>
            <AlertCircle
              className="w-10 h-10 mx-auto text-amber-400"
              strokeWidth={1.5}
            />
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-6">
              Session expired
            </h1>
            <p className="text-neutral-500 text-sm mt-2">
              Please retry the checkout from the audit page.
            </p>
            <a
              href="/free-audit"
              className="mt-6 inline-block text-white text-sm underline"
            >
              Back to audit
            </a>
          </>
        )}
        {(state.status === "error" || state.status === "timeout") && (
          <>
            <AlertCircle
              className="w-10 h-10 mx-auto text-red-400"
              strokeWidth={1.5}
            />
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-6">
              {state.status === "timeout" ? "Still pending" : "Something went wrong"}
            </h1>
            <p className="text-neutral-500 text-sm mt-2">
              If your card was charged, contact support and we'll resend the report manually.
            </p>
            <a
              href="/free-audit"
              className="mt-6 inline-block text-white text-sm underline"
            >
              Back to audit
            </a>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
